/**
 * Human-readable labels for backend field paths.
 *
 * The wizard UI writes thoughtful labels inline on each `<Field>`
 * ("Family (last) name", "Date you became a Permanent Resident",
 * …). Error rendering on the Review page and inline 422 banners
 * need the same wording — without this registry we'd be synthesising
 * "Family name" from the snake_case key, which drifts from what the
 * user actually sees on the input.
 *
 * The wizard `<Field label>` is markup-heavy (help buttons, hints),
 * so we keep these as pure strings rather than reusing the JSX. When
 * a label is added to the UI it should be added here too; nothing
 * enforces the link yet, but the surface is small.
 */

/** Keys upserted via /v1/cases/{id}/form-responses/... */
export const FORM_RESPONSE_LABELS: Record<string, string> = {
  // identifiers
  ssn: "SSN",
  a_number: "A-Number",
  uscis_online_account_number: "USCIS online account number",
  lpr_date: "Date you became a Permanent Resident",

  // eligibility / Part 2
  naturalization_basis: "Naturalization basis",
  naturalization_other_explanation: "Eligibility explanation",
  wants_name_change: "Wants legal name change",
  parent_usc_before_18: "Parent USC before 18",
  needs_disability_accommodation: "Disability accommodation needed",

  // other names used
  other_name_1_family: "Other name 1 — family name",
  other_name_1_given: "Other name 1 — given name",
  other_name_1_middle: "Other name 1 — middle name",
  other_name_2_family: "Other name 2 — family name",
  other_name_2_given: "Other name 2 — given name",
  other_name_2_middle: "Other name 2 — middle name",

  // new legal name (when change requested)
  new_legal_family_name: "New family name",
  new_legal_given_name: "New given name",
  new_legal_middle_name: "New middle name",

  // biographic
  ethnicity: "Ethnicity",
  race: "Race",

  // current spouse
  married_to_usc: "Married to a U.S. citizen",
  current_spouse_employer: "Current spouse's employer",
  current_spouse_armed_forces: "Spouse in armed forces",
  current_spouse_citizenship_basis: "Current spouse's citizenship basis",
  current_spouse_naturalization_date: "Current spouse's naturalization date",
  current_spouse_times_married: "Current spouse's prior marriages",

  // addresses (case-level prefs)
  current_address_in_care_of: "Current address — in care of",
  current_mailing_same_as_physical: "Mailing same as physical",
  mailing_address_in_care_of: "Mailing address — in care of",

  // contact / signature (applicant)
  daytime_phone: "Daytime telephone",
  mobile_phone: "Mobile telephone",
  signature_date: "Signature date",

  // selective service + SSA
  authorize_ssa_disclosure: "Authorize SSA disclosure",
  update_ssa_record: "Update SSA record",
  registered_selective_service: "Registered for Selective Service",
  selective_service_date: "Selective Service registration date",
  selective_service_number: "Selective Service number",

  // background "have you ever..."
  ever_been_member_communist_party: "Ever member of Communist Party",
  ever_been_terrorist: "Ever engaged in terrorist activity",
  ever_committed_crime_not_arrested: "Ever committed an unprosecuted crime",
  ever_failed_to_file_taxes: "Ever failed to file taxes",
  ever_owed_taxes: "Ever owed federal taxes",
  ever_persecuted_any_person: "Ever persecuted any person",
  has_criminal_history: "Criminal history",

  // gating (Part 1)
  has_prior_uscis_denial: "Prior USCIS denial",
  has_removal_proceedings: "Removal proceedings",
  continuous_us_residence: "Continuous U.S. residence",
  meets_lpr_duration: "Meets LPR duration",
  is_self_filing: "Self-filing",
  currently_in_us: "Currently in the U.S.",

  // oath
  willing_to_bear_arms: "Willing to bear arms",
  willing_to_civilian_service: "Willing to perform civilian service",
  willing_to_noncombatant_service: "Willing to perform noncombatant service",

  // misc
  nobility_titles: "Hereditary titles",
  uscis_field_office: "Preferred USCIS field office",

  // fee reduction
  fee_reduction_eligible: "Fee reduction eligibility",
  household_size: "Household size",
  household_earners: "Earners in household",
  household_income: "Household income",
  is_head_of_household: "Head of household",
  head_of_household_name: "Head of household name",

  // interpreter
  interpreter_family_name: "Interpreter's family name",
  interpreter_given_name: "Interpreter's given name",
  interpreter_org: "Interpreter's organisation",
  interpreter_phone: "Interpreter's daytime phone",
  interpreter_mobile: "Interpreter's mobile",
  interpreter_email: "Interpreter's email",
  interpreter_language: "Language interpreted",
  interpreter_signature_date: "Interpreter's signature date",

  // preparer
  preparer_family_name: "Preparer's family name",
  preparer_given_name: "Preparer's given name",
  preparer_org: "Preparer's organisation",
  preparer_phone: "Preparer's daytime phone",
  preparer_mobile: "Preparer's mobile",
  preparer_email: "Preparer's email",
  preparer_signature_date: "Preparer's signature date",
};

/** Fields on the Person row (PATCH /persons). */
export const PERSON_FIELD_LABELS: Record<string, string> = {
  given_name: "Given (first) name",
  middle_name: "Middle name",
  family_name: "Family (last) name",
  date_of_birth: "Date of birth",
  place_of_birth_city: "City of birth",
  place_of_birth_country: "Country of birth",
  country_of_citizenship: "Country of citizenship",
  country_of_nationality: "Country of nationality",
  current_immigration_status: "Current immigration status",
  gender: "Gender",
  marital_status: "Marital status",
  height_cm: "Height (cm)",
  weight_kg: "Weight (kg)",
  eye_color: "Eye color",
  hair_color: "Hair color",
  phone: "Daytime telephone",
  email: "Email address",
  role: "Role",
};

/** Sub-entity collection labels (persons[N].<entity>[M].field). */
export const SUBENTITY_LABELS: Record<string, string> = {
  addresses: "Address",
  employments: "Employment",
  travels: "Trip",
  marriages: "Marriage",
  parents: "Parent",
  children: "Child",
  criminal_events: "Criminal record",
};

/**
 * Field labels INSIDE a sub-entity. Several names collide with
 * PERSON_FIELD_LABELS (country_of_birth, full_name, …) — kept here
 * because the wording differs in context ("Spouse's country of
 * birth" vs "Country of birth").
 */
export const SUBENTITY_FIELD_LABELS: Record<string, string> = {
  // Address
  street_1: "Street address",
  street_2: "Apartment / suite",
  city: "City",
  state: "State / province",
  postal_code: "ZIP / postal code",
  country: "Country",
  start_date: "Lived here from",
  end_date: "Lived here until",
  is_mailing: "Mailing",
  is_physical: "Physical",

  // Employment
  employer_name: "Employer / school name",
  employer_address_1: "Employer street",
  employer_address_2: "Employer street 2",
  employer_city: "Employer city",
  employer_state: "Employer state",
  employer_postal_code: "Employer ZIP",
  employer_country: "Employer country",
  occupation_title: "Job title",

  // Travel
  departure_date: "Departure date",
  return_date: "Return date",
  countries_visited: "Countries visited",
  purpose: "Purpose of trip",

  // Marriage
  spouse_full_name: "Spouse's full name",
  spouse_date_of_birth: "Spouse's date of birth",
  spouse_country_of_birth: "Spouse's country of birth",
  spouse_current_citizenship: "Spouse's current citizenship",
  marriage_location_city: "Marriage city",
  marriage_location_country: "Marriage country",
  end_reason: "End reason",

  // Parent / Child
  full_name: "Full name",
  maiden_name: "Maiden name",
  country_of_birth: "Country of birth",
  country_of_citizenship: "Country of citizenship",
  date_of_death: "Date of death",
  current_immigration_status: "Current immigration status",

  // Criminal event
  event_type: "Event type",
  event_date: "Event date",
  location_city: "City",
  location_country: "Country",
  offense_description: "Offense description",
  disposition: "Disposition",
  sentence_details: "Sentence details",
};
